import {
  ACTOR_LABEL,
  DROP_LOCATION_LABEL,
  EVENT_LABEL,
  FAIL_REASON_LABEL,
  GOODS_DISPOSITION_LABEL,
  labelOf,
} from '@delivery/shared';
import { STATUS_META, type OrderEvent, type OrderStatus } from '../lib/types';
import { MapPin } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 订单时间轴。
 *
 * 数据来自 order_event —— append-only 的事实记录,不是从当前状态倒推的。
 * 客服接到「我的件到底在哪」的电话时,这一栏就是答案:
 * 谁、在什么时候、在哪里、做了什么。
 *
 * ★ 异常信号要显眼(远距离操作、系统自动改动),不能和正常事件长得一样 ——
 *   混在一起等于没记。见 docs/03-admin.md
 */
export function OrderTimeline({ events }: { events: OrderEvent[] }) {
  if (events.length === 0) {
    return <p className="text-sm text-slate-400 py-4">暂无事件</p>;
  }

  // 倒序:最新的在最上面。查订单时关心的永远是"现在到哪了"
  const rows = [...events].sort((a, b) => b.seq - a.seq);

  return (
    /*
     * ★ 给时间轴一个名字。抽屉里不止一处列表(通知记录也是 li),
     *   用例如果数「抽屉里所有的 listitem」,别处加一条就会把它撞红 ——
     *   而那种红和被测的东西毫无关系。
     */
    <ol className="relative" aria-label="订单时间轴">
      {rows.map((e, i) => {
        const isLatest = i === 0;
        const meta = e.toStatus ? STATUS_META[e.toStatus as OrderStatus] : null;
        const far = e.distanceToTargetM != null && e.distanceToTargetM > 1000;

        return (
          <li key={e.seq} className="relative pl-7 pb-5 last:pb-0">
            {/* 竖线:最后一项不画,否则会拖出一截空线 */}
            {i < rows.length - 1 && (
              <span
                className="absolute left-[7px] top-4 bottom-0 w-px bg-slate-200 dark:bg-slate-700"
                aria-hidden
              />
            )}
            <span
              className={`absolute left-0 top-[5px] w-[15px] h-[15px] rounded-full border-2
                ${isLatest
                  ? 'bg-brand-600 border-brand-200 dark:border-brand-900'
                  : 'bg-slate-300 dark:bg-slate-600 border-white dark:border-slate-800'}`}
              aria-hidden
            />

            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {labelOf(EVENT_LABEL, e.type)}
              </span>
              {meta && (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${meta.cls}`}>
                  {meta.label}
                </span>
              )}
              <span className="text-xs text-slate-400 tabular-nums">
                {new Date(e.createdAt).toLocaleString('zh-CN', {
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            </div>

            <div className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
              {labelOf(ACTOR_LABEL, e.actorType)}
              {e.actorName && ` · ${e.actorName}`}
              {e.lat != null && e.lng != null && (
                <a
                  className="ml-2 text-brand-600 dark:text-brand-500 hover:underline"
                  href={`https://www.google.com/maps?q=${e.lat},${e.lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <InlineIcon icon={MapPin} /> 操作位置
                </a>
              )}
            </div>

            {/* ★ 远距离操作单独标红 —— 「在离收货地 5km 外点已送达」是典型的作弊模式,
                   埋在正常事件里就等于没记 */}
            {far && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                ⚠ 操作位置距目标 {(e.distanceToTargetM! / 1000).toFixed(1)}km
              </p>
            )}

            <PayloadNotes payload={e.payload} />
          </li>
        );
      })}
    </ol>
  );
}

/** payload 里各动作自己的细节。只挑对人有意义的显示,不要把整个 JSON 倒出来 */
function PayloadNotes({ payload }: { payload: Record<string, unknown> }) {
  const notes: string[] = [];
  const s = (k: string) => (typeof payload[k] === 'string' ? (payload[k] as string) : null);

  const note = s('note');
  if (note) notes.push(note);

  const reason = s('reason');
  if (reason) notes.push(`原因:${reason}`);

  const failReason = s('failReason');
  if (failReason) notes.push(`失败原因:${labelOf(FAIL_REASON_LABEL, failReason)}`);

  const signedByName = s('signedByName');
  if (signedByName) notes.push(`签收人:${signedByName}`);

  const dropLocation = s('dropLocation');
  if (dropLocation) notes.push(`放置位置:${labelOf(DROP_LOCATION_LABEL, dropLocation)}`);

  const disposition = s('goodsDisposition');
  if (disposition) notes.push(`货物去向:${labelOf(GOODS_DISPOSITION_LABEL, disposition)}`);

  // 围栏被绕过必须留痕:司机不在取货点却完成了取货
  const override = s('geofenceOverride');
  if (override) notes.push(`⚠ 绕过地理围栏:${override}`);

  const skip = s('proofSkipReason');
  if (skip) notes.push(`⚠ 跳过拍照:${skip}`);

  if (notes.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {notes.map((n) => (
        <li
          key={n}
          className={`text-sm ${
            n.startsWith('⚠') ? 'text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'
          }`}
        >
          {n}
        </li>
      ))}
    </ul>
  );
}
